import cn from 'classnames/bind';
import Head from 'next/head';
import { Col, Container, Row, SEO } from 'ui';
import BlogCard from 'ui/components/blog/BlogCard';
import CategoryListSection from 'ui/components/blog/CategoryListSection';
import BlogCategorySlider from 'ui/layouts/blog/BlogCategorySlider';
import ResourcePageLayout from 'ui/layouts/blog/ResourcePageLayout';
import { getNewLabel } from 'utils';
import { dateformat } from 'utils/datetime';
import getJsonLdBlog from 'utils/jsonLd/getJsonLdBlog';
import { generateBlogPostUrl, host } from 'utils/jsonLd/utils';
import getJsonLdWebPage from 'utils/jsonLd/webPage';
import { getArticleHref } from 'utils/links';

import s from './ResourcesBlog.module.scss';

const type = process.env.NEXT_PUBLIC_LANDING_TYPE || '';
const cx = cn.bind(s);

const meta = {
  title: 'Blog',
  description:
    'Our blog covers topics about the 16 personality types, ranging from their strengths and weaknesses to compatibility and beyond.',
};

const headerData = {
  title: 'MyPersonality Blog',
  description:
    'Welcome to our personalities blog, where we dive into all different personality types, compatibility, career matches, and more. Explore different topics for each personality type to discover more about yourself and those around you. Get practical advice for personal growth, relationships, and the workplace—all in one spot.',
};

const Blog = ({
  posts,
  jsonLd,
  categories,
  layout: Layout = ResourcePageLayout,
}) => (
  <>
    <SEO title={meta.title} description={meta.description} />
    <Head>
      <script
        type="application/ld+json"
        key="product-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </Head>
    <Layout
      headerTitle={headerData.title}
      headerInfo={headerData.description}
      topSpacingClassName={s.topSpacing}
    >
      <Container className={cx('content')}>
        <BlogCategorySlider categories={categories} />
        <Row>
          {posts.map((post) => (
            <Col
              key={post.slug}
              lg={post.isExtended ? 8 : 4}
              className={cx('card')}
            >
              <BlogCard
                isExtended={post.isExtended}
                date={dateformat.short(post.createdAt)}
                label={getNewLabel(post.createdAt)}
                href={getArticleHref(post.slug, { isLanding: !!type })}
                title={post.title}
                text={post.description}
                blogTopic={post.blogTopic}
                author={post.author}
                poster={post.image}
                posterMeta={post.imageData}
              />
            </Col>
          ))}
        </Row>
        <CategoryListSection categories={categories} />
      </Container>
    </Layout>
  </>
);

Blog.prettyName = 'Blog';

export const getStaticProps = async () => {
  const BlogModel = await (async () => {
    if (type) {
      const { default: model } = await import('api/models/LandingBlogPost');
      return model;
    }
    const { default: model } = await import('api/models/BlogPost');
    return model;
  })();
  const postsPromise = BlogModel.get(type);
  const categoriesPromise = BlogModel.getCategories();

  const [posts, categories] = await Promise.all([
    postsPromise,
    categoriesPromise,
  ]);

  const postsWithUrl = posts.map((post) => ({
    ...post,
    url: generateBlogPostUrl(type, post.slug),
  }));

  const jsonLd = getJsonLdWebPage({
    name: meta.title,
    description: meta.description,
    url: `${host}/blog`,
    mainEntity: getJsonLdBlog({ postsWithUrl }),
  });

  return {
    props: {
      posts,
      categories,
      jsonLd,
    },
    revalidate: 30 * 60,
  };
};
export default Blog;
